import { type AwilixContainer, asFunction, asValue, asClass } from 'awilix';
import { createTypedContainer } from './helpers';
import { media } from '../webrtc/media';
import { session } from '../webrtc/session';
import {
  getFrontendConfigLoader,
  getGrowthBookConfigLoader,
} from '@doxyme/config';
import { ServiceTokens } from './services';
import { callServiceFactory } from '../services/call/call.factory';
import { QualityOfService } from '../services/call/qos';
import { callActor } from '../features/call';
import { realtimeInstance } from '../features/realtime/realtime.slice';
import { PatientRealtimeService } from '../services/call/realtime/patientRealtime.service';
import { ProviderRealtimeService } from '../services/call/realtime/providerRealtime.service';
import { getLogger } from '../libs/logger';
import { CommonRealtimeService } from '../services/call/realtime/commonRealtime.service';
import { store } from '../stores/redux';
import { FileHostingService } from '../services/fileTransfer';
import { PhotoCaptureService } from '../services/photoCapture/photoCapture.service';
import { CallHistoryService } from '../services/call/history/callHistory.service';
import { TranscriptionService } from '../services/transcription';
import { ContainerContext } from './types';

export const services = {
  // Workaround for DevTools warnings triggered by accessing `window` via Awilix Proxy injection.
  window: asValue(undefined),
  $container: { resolve: (c: AwilixContainer) => c },
  $context: asValue<ContainerContext>(null),
  [ServiceTokens.ReduxStore]: asValue(store),
  [ServiceTokens.FileHosting]: asClass(FileHostingService),
  [ServiceTokens.PhotoCapture]: asClass(PhotoCaptureService).singleton(),
  [ServiceTokens.Media]: asValue(media),
  [ServiceTokens.Session]: asValue(session),
  [ServiceTokens.Config]: asFunction(getFrontendConfigLoader).singleton(),
  [ServiceTokens.GrowthBook]: asFunction(getGrowthBookConfigLoader).singleton(),
  [ServiceTokens.Call]: asFunction(callServiceFactory)
    .disposer((c) => void c.destroy())
    .singleton(),
  [ServiceTokens.CallHistory]: asClass(CallHistoryService, {
    injector: () => ({
      [ServiceTokens.Logger]: getLogger('CallHistoryService'),
    }),
  }).singleton(),
  [ServiceTokens.Realtime]: asValue(realtimeInstance),
  [ServiceTokens.PatientRealtime]: asClass(PatientRealtimeService, {
    injector: () => ({
      [ServiceTokens.Logger]: getLogger('PatientRealtimeService'),
    }),
  }).singleton(),
  [ServiceTokens.ProviderRealtime]: asClass(ProviderRealtimeService, {
    injector: () => ({
      [ServiceTokens.Logger]: getLogger('ProviderRealtimeService'),
    }),
  }).singleton(),
  [ServiceTokens.CommonRealtime]: asClass(CommonRealtimeService, {
    injector: () => ({
      [ServiceTokens.Logger]: getLogger('CommonRealtimeService'),
    }),
  }).singleton(),
  [ServiceTokens.Transcription]: asClass(TranscriptionService, {
    injector: () => ({
      [ServiceTokens.Logger]: getLogger('TranscriptionService'),
    }),
  }).singleton(),
  [ServiceTokens.QualityOfService]: asClass(QualityOfService, {
    injector: () => ({
      [ServiceTokens.Logger]: getLogger('QualityOfService'),
    }),
  })
    .disposer((qos) => qos.dispose())
    .singleton(),
  // Temporary solution. Actor should be encapsulated in its own service (call)
  [ServiceTokens.CallActor]: asValue(callActor),
};
export const serviceContainer = createTypedContainer(services);
